/**
 * Shared styles for the Family of Companies overview cards, `./CompanyCard.tsx` (desktop, with
 * the hover panel) and `./CompanyCardMobile.tsx`. The hover rules in
 * {@link CardContainerWithHover} select the children by the class names `logo-container`, `logo`
 * and `text-container` that `CompanyCard.tsx` sets, so those names change together.
 * `#002855` is a literal here, not a theme colour.
 */
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ITheme } from "@ryerson/frontend.theme";

/** The 360px desktop card. On hover: solid blue background, logo hidden, text panel shown. */
export const CardContainerWithHover = styled.div`
	max-height: 360px;
	max-width: 360px;
	height: 360px;
	width: 360px;
	margin-top: 30px;
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center center;
	box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.25);
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	cursor: pointer;
	&:hover {
		background-image: none;
		background-color: #002855; /* Solid blue background */
		> .logo-container > .logo {
			opacity: 0;
			visibility: hidden;
			transition: opacity 0.25s ease-in-out, visibility 0.25s ease-in-out;
		}
		> .text-container {
			opacity: 1;
			visibility: visible;
		}
	}
`;

/** The 300px mobile card, centred, with no hover state. */
export const CardContainerMobile = styled.div`
	height: 300px;
	width: 300px;
	margin: 30px auto;
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center center;

	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`;

interface GradientLayersProps {
	company: {
		mainFoCImage: { file: { url: string } };
	};
	theme: ITheme;
}

/**
 * The card background, three layers from top to bottom: a transparent-black-transparent vertical
 * gradient, the company's `mainFoCImage`, and a solid dark grey that shows while the image loads
 * or if it fails.
 */
export const CSSGradientLayers = ({ company, theme }: GradientLayersProps) => css`
	background-image: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0) 0%,
			#000 49.52%,
			rgba(0, 0, 0, 0) 97.6%
		),
		url(${company.mainFoCImage.file.url}),
		linear-gradient(${theme.colors.primary.darkGray}, ${theme.colors.primary.darkGray});
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center center;
`;

/** The logo, centred on the card and capped at 60% of its width and 45% of its height. */
export const CSSLogoWithDropShadow = css`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	max-width: 60%;
	max-height: 45%;
	transition: opacity 1s ease-in-out;
	opacity: 1;
	z-index: 0;
	filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.25));
`;

/** The blurb and button panel over the desktop card, hidden until the card is hovered. */
export const TextContainerWithHover = styled.div`
	position: absolute;
	top: 0;
	text-align: center;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	padding: 40px 35px;
	visibility: hidden;
	&:hover {
		background-image: none;
		background-color: #002855;
	}
`;

/** Centres the "Learn More" button 150px below the blurb. */
export const ButtonLinkContainer = styled.div`
    text-align: center;
    width: 100%;
    margin-top: 150px;
`;


/** Full-size positioning box for the logo on the mobile card. */
export const LogoContainerMobile = styled.div`
	position: relative;
	height: 100%;
	width: 100%;
`;


/** Full-size box that centres the logo on the desktop card. */
export const LogoContainerWithHover = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	position: absolute;
	transition: background 1.25s ease-in-out;
	&:hover {
		background-image: none;
		background-color: #002855; /* Solid blue background */
	}
`;

/**
 * A larger centred-logo style. Nothing imports it; both cards use {@link CSSLogoWithDropShadow}.
 */
export const CSSLogoStyleMobile = css`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	max-width: 65%;
	max-height: 50%;
`;

